import { SetupInterruptionRecovery } from "./SetupInterruptionRecovery";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ArrowBigRightDash, ArrowLeft, ArrowRight, HardDrive, Loader2, Plus, RefreshCw, Settings2 } from "lucide-react";
import { DataTable } from "@/components/shared/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStorageList } from "@/features/storages/hooks/useStorages";
import { isStorageIntegratedAvailable } from "@/features/storages/storage-ux";
import type { AppBindingSummary } from "@/types/api";
import { getAppBindingModeLabel } from "./app-binding-ux";
import {
  AppBindingStatusBadges,
  isAppAvailabilityFilter,
  resolveAvailabilityState,
  resolveAppRuntimeState,
  resolveAppSettingState,
} from "./app-binding-status-ui";
import { useAppBindingList, useSecureAccessSettings } from "./hooks/useAppBindings";
import { useKintoneEnvironment } from "./hooks/useKintoneEnvironment";
import { useAuth } from "@/hooks/useAuth";
import { useInitialKintoneSetupSession } from "./InitialKintoneSetupSessionContext";
import {
  clientCertificatePasswordValidationError,
  resolveClientCertificateExpiryState,
} from "./secure-access-ux";
import { ClientCertificateExpiryBanner } from "./ClientCertificateExpiryBanner";
import { KintoneEnvironmentRepairPanel } from "./KintoneEnvironmentRepairPanel";

function filterLabel(t: TFunction, key: string, value: string) {
  const translated = t(`filters.values.${key}.${value}`, { defaultValue: "" });
  return translated || value;
}

function formatUpdatedAt(value?: string) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("ja-JP");
}

export function AppBindingListPage({ initialRegistration = false }: { initialRegistration?: boolean }) {
  const { t } = useTranslation("app-bindings");
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialSetupSession = useInitialKintoneSetupSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subdomain, setSubdomain] = useState("");
  const [subdomainError, setSubdomainError] = useState<string | null>(null);
  const [connectionAccessKind, setConnectionAccessKind] =
    useState<"standard" | "secure_access">("standard");
  const [clientCertFile, setClientCertFile] = useState<File | null>(null);
  const [clientCertPassphrase, setClientCertPassphrase] = useState("");
  const [connectionAccessError, setConnectionAccessError] = useState<string | null>(null);
  const [registrationStep, setRegistrationStep] = useState<"subdomain" | "method">("subdomain");
  const { environment, isLoading: environmentLoading, error: environmentError, refresh: refreshEnvironment } = useKintoneEnvironment();
  const isAdmin = user?.currentMembership?.role === "customer_admin";
  const { view: secureAccessView } = useSecureAccessSettings({
    enabled: environment?.kintoneConnectionState === "registered",
  });
  const certificateExpiryState = resolveClientCertificateExpiryState(
    secureAccessView?.connectionKind === "secure_access",
    secureAccessView?.expiresAt ?? null,
  );
  const availabilityQuery = searchParams.get("availability");
  const availability = isAppAvailabilityFilter(availabilityQuery) ? availabilityQuery : null;
  const filters = useMemo(() => {
    const next: Record<string, string> = {};
    for (const key of ["settingReflectionState", "pluginVersionState", "blockedReason", "appSettingState"] as const) {
      const value = searchParams.get(key);
      if (value) next[key] = value;
    }
    return next;
  }, [searchParams]);
  const activeFilters = useMemo(() =>
    [...Object.entries(filters), ...(availability ? [["availability", availability] as [string, string]] : [])]
      .map(([key, value]) => ({ key, value, label: filterLabel(t, key, value) })),
  [availability, filters, t]);
  const { items, isLoading, error: appBindingsError, hasNext, hasPrev, goNext, goPrev, refresh: refreshAppBindings } = useAppBindingList(filters);
  const repairOperationId = environment?.repairOperation?.operationId ?? null;
  const visibleItems = availability
    ? items.filter((binding) => resolveAvailabilityState(binding) === availability)
    : items;
  const {
    items: storages,
    isLoading: storagesLoading,
    error: storageError,
    refresh: refreshStorages,
  } = useStorageList({ limit: "100" });
  const hasReadyStorage = storages.some(isStorageIntegratedAvailable);
  const waitingForRuntime = storages.some((storage) =>
    storage.storageLifecycleState === "active" && storage.settingState === "ready" && !storage.runtimeStatusSnapshot);
  const storageReadinessLoading = storagesLoading || waitingForRuntime;

  useEffect(() => {
    if (initialRegistration && registrationStep === "method" && !storageReadinessLoading && !storageError && hasReadyStorage && environment?.commercial.runtimeAllowed) {
      navigate(`/console/app-bindings/new?setupMethod=easy&candidateSubdomain=${encodeURIComponent(subdomain)}`, { replace: true });
    }
  }, [initialRegistration, registrationStep, storageReadinessLoading, storageError, hasReadyStorage, environment?.commercial.runtimeAllowed, subdomain, navigate]);

  const columns = [
    {
      key: "kintoneApp",
      header: t("columns.kintoneApp"),
      render: (binding: AppBindingSummary) => <div className="flex flex-col"><span className="font-medium">{binding.kintoneAppName || t("columns.kintoneAppNameFallback", { appId: binding.kintoneAppId })}</span><span className="font-mono text-xs text-muted-foreground">{t("columns.kintoneAppId")}: {binding.kintoneAppId}</span></div>,
    },
    { key: "storage", header: t("columns.storageDisplayName"), render: (binding: AppBindingSummary) => binding.storageDisplayName || t("columns.storageDisplayNameUnavailable") },
    { key: "mode", header: t("columns.mode"), render: (binding: AppBindingSummary) => getAppBindingModeLabel(binding.modeId) },
    {
      key: "readiness",
      header: t("columns.readiness"),
      render: (binding: AppBindingSummary) => <div className="space-y-1.5"><AppBindingStatusBadges binding={binding} t={t} /><div className="text-xs text-muted-foreground"><div>{t("statusBreakdownLabels.setting")}: {t(`appSettingStatusLabels.${resolveAppSettingState(binding)}`)}</div><div>{t("statusBreakdownLabels.runtime")}: {t(`runtimeStatusLabels.${resolveAppRuntimeState(binding)}`)}</div></div></div>,
    },
    { key: "fields", header: t("columns.targetFieldCount"), render: (binding: AppBindingSummary) => `${binding.targetFieldCount ?? 0}` },
    { key: "updated", header: t("columns.updatedAt"), render: (binding: AppBindingSummary) => formatUpdatedAt(binding.updatedAt) },
  ];

  function clearFilters() {
    const next = new URLSearchParams(searchParams);
    for (const key of ["settingReflectionState", "pluginVersionState", "blockedReason", "appSettingState", "availability"]) next.delete(key);
    setSearchParams(next);
  }

  if (environmentLoading) {
    return <div className="p-6"><h1 className="text-2xl font-bold">kintoneアプリ</h1><div className="mt-10 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />kintone環境を確認しています</div></div>;
  }

  if (!environment || environmentError) {
    return <div className="p-6"><h1 className="text-2xl font-bold">kintoneアプリ</h1><div className="mt-6 max-w-3xl rounded-md border border-destructive/30 bg-destructive/5 p-5"><h2 className="font-semibold">kintone環境を確認できません</h2><p className="mt-2 text-sm text-muted-foreground">{environmentError ?? "再読み込みしてください。"}</p><Button className="mt-4" variant="outline" onClick={() => void refreshEnvironment()}><RefreshCw className="mr-2 h-4 w-4" />再読み込み</Button></div></div>;
  }

  if (environment.commercial.commercialState === "canceled") {
    return <div className="p-6"><h1 className="text-2xl font-bold">kintoneアプリ</h1><div className="mt-6 max-w-3xl rounded-md border border-amber-300 bg-amber-50 p-5"><h2 className="font-semibold">サービスは解約済みです</h2><p className="mt-2 text-sm text-muted-foreground">監査情報の確認と削除処理以外のkintone環境操作はできません。</p></div></div>;
  }

  if (!initialRegistration && (environment.kintoneConnectionState === "unregistered" || !environment.kintoneSubdomain)) {
    return <div className="p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold">kintoneアプリ</h1>

      </div>
      {!isAdmin ? <p className="mb-4 text-sm text-muted-foreground">kintone環境の登録には管理者権限が必要です。組織管理者に登録を依頼してください。</p> : null}
      {appBindingsError ? <div role="alert">一覧を取得できません。<Button variant="outline" onClick={() => void refreshAppBindings()}>一覧を再読み込み</Button></div> :
        !isLoading && !storageReadinessLoading && environment.appBindingCount === 0 && isAdmin ? (
          storageError ? <Empty onboarding={!storageError} icon={<HardDrive className="h-6 w-6" />} title={t("storageReadinessUnknown.title")} body={t("storageReadinessUnknown.body")} action={<Button variant="outline" onClick={() => void refreshStorages()}>再読み込み</Button>} /> :
          hasReadyStorage ? <OnboardingChoices disabled={!environment.commercial.runtimeAllowed} onEasy={() => navigate("/console/app-bindings/new/environment")} /> :
          <Empty onboarding={!storageError} icon={<HardDrive className="h-6 w-6" />} title={t("storageNotReady.title")} body={t("storageNotReady.body")} action={<Button className="bg-amber-300 text-amber-950 hover:bg-amber-400" onClick={() => navigate("/console/storages")}>{t("storageNotReady.cta")}</Button>} />
        ) : <DataTable columns={columns} items={visibleItems} isLoading={isLoading} hasNext={hasNext} hasPrev={hasPrev} onNext={goNext} onPrev={goPrev} emptyMessage="登録済みのアプリはありません。" keyExtractor={(binding) => binding.appBindingId} />}
    </div>;
  }

  if (environment.kintoneConnectionState === "unregistered" || !environment.kintoneSubdomain) {
    if (!isAdmin) {
      return (
        <div className="p-6">
          <h1 className="text-2xl font-bold tracking-tight">kintoneアプリ</h1>
          <div className="mt-6 max-w-2xl border-t pt-6">
            <Empty
              icon={<Settings2 className="h-6 w-6" />}
              title="kintone環境が未登録です"
              body="kintone環境の登録には管理者権限が必要です。組織管理者に登録を依頼してください。"
              action={<span className="text-sm text-muted-foreground">登録後、この画面から手動セットアップを利用できます。</span>}
            />
          </div>
        </div>
      );
    }

    const continueToMethodSelection = async () => {
      const normalized = subdomain.trim().toLowerCase()
        .replace(/\.(?:s\.)?cybozu\.com$/, "");
      if (!/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/.test(normalized)) {
        setSubdomainError("3〜63文字のkintoneサブドメインを入力してください。");
        return;
      }
      const customerId = user?.serviceSubscription?.customerId;
      if (!customerId) {
        setConnectionAccessError("選択中の組織を確認できません。再読み込みしてください。");
        return;
      }
      if (connectionAccessKind === "secure_access" && !clientCertFile) {
        setConnectionAccessError("クライアント証明書（.pfx / .p12）を選択してください。");
        return;
      }
      if (clientCertFile && clientCertFile.size > 1_048_576) {
        setConnectionAccessError("クライアント証明書は1 MiB以下にしてください。");
        return;
      }
      const passwordValidationError = connectionAccessKind === "secure_access"
        ? clientCertificatePasswordValidationError(clientCertPassphrase)
        : null;
      if (passwordValidationError) {
        setConnectionAccessError(passwordValidationError);
        return;
      }
      try {
        if (connectionAccessKind === "standard") {
          initialSetupSession.configure({
            customerId,
            targetSubdomain: normalized,
            connectionAccess: { kind: "standard" },
          });
        } else {
          initialSetupSession.configure({
            customerId,
            targetSubdomain: normalized,
            connectionAccess: {
              kind: "secure_access",
              clientCert: new Uint8Array(await clientCertFile!.arrayBuffer()),
              passphrase: clientCertPassphrase,
            },
          });
        }
      } catch {
        setConnectionAccessError("クライアント証明書を読み込めませんでした。");
        return;
      }
      setSubdomain(normalized);
      setSubdomainError(null);
      setConnectionAccessError(null);
      setRegistrationStep("method");
    };

    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate("/console/app-bindings")}><ArrowLeft className="mr-2 h-4 w-4" />一覧へ戻る</Button>
        <h1 className="text-2xl font-bold tracking-tight">kintoneアプリの追加</h1>
        <div className="mt-6 max-w-2xl border-t pt-6">
          {registrationStep === "subdomain" ? (
            <form noValidate className="space-y-5" onSubmit={(event) => { event.preventDefault(); void continueToMethodSelection(); }}>
              <div><h2 className="text-lg font-semibold">kintone環境を登録</h2><p className="mt-1 text-sm text-muted-foreground">この組織で使用するkintoneサブドメインと接続方法を入力してください。</p></div>
              <div className="space-y-2">
                <Label htmlFor="kintone-subdomain">kintoneサブドメイン</Label>
                <div className="flex max-w-lg items-center gap-2"><Input id="kintone-subdomain" value={subdomain} onChange={(event) => { setSubdomain(event.target.value); setSubdomainError(null); }} placeholder="example" autoComplete="off" autoFocus aria-invalid={Boolean(subdomainError)} aria-describedby={subdomainError ? "kintone-subdomain-error" : undefined} /><span className="shrink-0 text-sm text-muted-foreground">{connectionAccessKind === "secure_access" ? ".s.cybozu.com" : ".cybozu.com"}</span></div>
              </div>
              <fieldset className="space-y-3">
                <legend className="text-sm font-medium">kintoneへの接続方法</legend>
                <label className="flex items-start gap-2 text-sm">
                  <input type="radio" name="connection-access" value="standard" checked={connectionAccessKind === "standard"} onChange={() => { setConnectionAccessKind("standard"); setConnectionAccessError(null); }} />
                  <span><span className="font-medium">通常接続</span><span className="block text-xs text-muted-foreground">{subdomain || "example"}.cybozu.com へ接続します。</span></span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input type="radio" name="connection-access" value="secure_access" checked={connectionAccessKind === "secure_access"} onChange={() => { setConnectionAccessKind("secure_access"); setConnectionAccessError(null); }} />
                  <span><span className="font-medium">セキュアアクセス</span><span className="block text-xs text-muted-foreground">{subdomain || "example"}.s.cybozu.com へクライアント証明書を提示します。</span></span>
                </label>
              </fieldset>
              {connectionAccessKind === "secure_access" ? (
                <div className="space-y-4 rounded-md border bg-muted/20 p-4">
                  <div className="space-y-2">
                    <Label htmlFor="initial-client-certificate">クライアント証明書（.pfx / .p12）</Label>
                    <input id="initial-client-certificate" type="file" accept=".pfx,.p12,application/x-pkcs12" onChange={(event) => { setClientCertFile(event.target.files?.[0] ?? null); setConnectionAccessError(null); }} className="block w-full text-sm" />
                    <p className="text-xs text-muted-foreground">登録が完了するまでブラウザメモリだけで保持し、画面には再表示しません。</p>
                  </div>
                  <div className="space-y-2"><Label htmlFor="initial-client-certificate-passphrase">クライアント証明書のパスワード（必須）</Label><Input id="initial-client-certificate-passphrase" type="password" value={clientCertPassphrase} onChange={(event) => setClientCertPassphrase(event.target.value)} autoComplete="off" required /></div>
                </div>
              ) : null}
              {subdomainError ? <p id="kintone-subdomain-error" role="alert" className="text-sm text-destructive">{subdomainError}</p> : null}
              {connectionAccessError ? <p role="alert" className="text-sm text-destructive">{connectionAccessError}</p> : null}
              <Button type="submit">次へ<ArrowRight className="ml-2 h-4 w-4" /></Button>
            </form>
          ) : (
            <section className="space-y-5" aria-labelledby="setup-method-title">
              <Button type="button" variant="ghost" className="-ml-3" onClick={() => { setRegistrationStep("subdomain"); setSubdomainError(null); }}><ArrowLeft className="mr-2 h-4 w-4" />サブドメインを変更する</Button>
              <div>
                <h2 id="setup-method-title" tabIndex={-1} className="text-lg font-semibold outline-none">セットアップの準備</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  接続先は <span className="font-medium text-foreground">{subdomain}{initialSetupSession.session?.connectionAccessKind === "secure_access" ? ".s.cybozu.com" : ".cybozu.com"}</span> です。
                  接続を確認してから登録します。
                </p>
              </div>
              {storageReadinessLoading ? (
                <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  利用可能なストレージを確認しています
                </div>
              ) : storageError ? (
                <Empty
                  icon={<HardDrive className="h-6 w-6" />}
                  title="ストレージの利用可否を確認できません"
                  body="設定方法を開始せず、ストレージの最新状態を再取得してください。"
                  action={<Button variant="outline" onClick={() => void refreshStorages()}><RefreshCw className="mr-2 h-4 w-4" />再読み込み</Button>}
                />
              ) : hasReadyStorage ? (
                <p role="status">簡単セットアップを開いています…</p>
              ) : (
                <Empty
                  icon={<HardDrive className="h-6 w-6" />}
                  title="先にストレージを利用可能にしてください"
                  body="kintoneアプリの設定には、利用可能なストレージが1件以上必要です。ストレージの準備後にこの画面へ戻り、サブドメインを確認して設定方法を選んでください。"
                  action={<Button variant="outline" onClick={() => navigate("/console/storages")}>ストレージへ</Button>}
                />
              )}
            </section>
          )}
          </div>
      </div>
    );
  }

  const noBinding = !isLoading && !storageReadinessLoading && environment.appBindingCount === 0 && activeFilters.length === 0;
  const runtimeAllowed = environment.commercial.runtimeAllowed;
  return (
    <div className="p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div><h1 className="text-2xl font-bold">kintoneアプリ</h1><p className="mt-1 text-sm text-muted-foreground">kintoneサブドメイン: {environment.kintoneSubdomain}</p></div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isAdmin ? (
            <Button
              variant="outline"
              onClick={() => navigate("/console/app-bindings/environment")}
            ><ArrowBigRightDash className="mr-2 h-4 w-4" fill="currentColor" strokeWidth={2.5} />kintone環境を移行</Button>
          ) : null}
          {environment.appBindingCount > 0 ? <Button onClick={() => navigate("/console/app-bindings/new")} disabled={!hasReadyStorage || !runtimeAllowed}><Plus className="mr-2 h-4 w-4" />{t("newBinding")}</Button> : null}
        </div>
      </div>
      {secureAccessView?.connectionKind === "secure_access" && certificateExpiryState === "expired" ? (
        <ClientCertificateExpiryBanner
          onOpenSettings={
            isAdmin
              ? () => navigate("/console/service-subscription")
              : undefined
          }
        />
      ) : null}
      {!runtimeAllowed ? <ServiceBlockedBanner commercialState={environment.commercial.commercialState} isAdmin={isAdmin} onOpenServiceSubscription={() => navigate("/console/service-subscription")} /> : null}
      {isAdmin && repairOperationId ? (
        <KintoneEnvironmentRepairPanel
          operationId={repairOperationId}
          onAppBindingChanged={() => {
            void Promise.all([refreshAppBindings(), refreshEnvironment()]);
          }}
        />
      ) : null}
      {activeFilters.length > 0 ? <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm"><span className="font-semibold">{t("filters.activeTitle")}</span>{activeFilters.map((filter) => <span key={filter.key} className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs">{filter.label}</span>)}<Button variant="ghost" size="sm" onClick={clearFilters}>{t("filters.clear")}</Button></div> : null}
      <SetupInterruptionRecovery />
      {appBindingsError ? <div role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 p-4">
        <p className="font-medium">アプリ設定一覧を取得できませんでした。</p>
        <p className="mt-1 text-sm">{appBindingsError}</p>
        <Button type="button" variant="outline" className="mt-3" disabled={isLoading} onClick={() => void refreshAppBindings()}>一覧を再読み込み</Button>
      </div> : null}
      {noBinding && !appBindingsError && hasReadyStorage ? <OnboardingChoices disabled={!runtimeAllowed} onEasy={() => navigate("/console/app-bindings/new?setupMethod=easy")} /> : noBinding && !appBindingsError && !hasReadyStorage ? <Empty onboarding={!storageError} icon={<HardDrive className="h-6 w-6" />} title={storageError ? t("storageReadinessUnknown.title") : t("storageNotReady.title")} body={storageError ? t("storageReadinessUnknown.body") : t("storageNotReady.body")} action={storageError ? <Button variant="outline" onClick={() => void refreshStorages()}><RefreshCw className="mr-2 h-4 w-4" />再読み込み</Button> : <Button className="bg-amber-300 text-amber-950 hover:bg-amber-400" onClick={() => navigate("/console/storages")}>{t("storageNotReady.cta")}</Button>} /> : <DataTable columns={columns} items={visibleItems} isLoading={isLoading || storageReadinessLoading} hasNext={hasNext} hasPrev={hasPrev} onNext={goNext} onPrev={goPrev} emptyMessage={appBindingsError ? "一覧を取得できません。再読み込みしてください。" : activeFilters.length ? t("filters.empty") : "登録済みのアプリはありません。"} onRowClick={(binding) => navigate(`/console/app-bindings/${binding.kintoneAppId}`)} keyExtractor={(binding) => binding.appBindingId} />}
    </div>
  );
}

function ServiceBlockedBanner({ commercialState, isAdmin, onOpenServiceSubscription }: {
  commercialState: string;
  isAdmin: boolean;
  onOpenServiceSubscription: () => void;
}) {
  const reason = commercialState === "payment_required"
    ? "お支払いを確認できないため、サービスを停止しています。"
    : commercialState === "expired"
      ? "お試し期間が終了したため、サービスを停止しています。"
      : "サービスを利用できない状態です。";
  const action = commercialState === "payment_required" ? "お支払いを確認" : commercialState === "expired" ? "有料プランを申し込む" : "利用状態を確認";
  return <div role="alert" className="mb-5 max-w-3xl rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm"><p className="font-semibold">現在はアプリを追加できません</p><p className="mt-1 text-muted-foreground">{reason}登録済みの環境とアプリ設定は確認できます。{isAdmin ? "" : "再開するには、組織の管理者に連絡してください。"}</p>{isAdmin ? <Button className="mt-3" size="sm" onClick={onOpenServiceSubscription}>{action}<ArrowRight className="ml-2 h-4 w-4" /></Button> : null}</div>;
}

function OnboardingChoices({ disabled, onEasy }: { disabled: boolean; onEasy: () => void }) {
  return <section className="max-w-3xl rounded-md border border-amber-300 bg-amber-50 p-6 text-amber-950"><h2 className="text-lg font-semibold">最初のアプリを追加</h2><p className="mt-2 text-sm text-amber-900">添付ファイルを外部ストレージに保存するkintoneアプリを設定します。「アプリを追加」から接続先と対象アプリを設定してください。</p><Button className="mt-4 bg-amber-300 text-amber-950 hover:bg-amber-400" onClick={onEasy} disabled={disabled}>アプリを追加<ArrowRight className="ml-2 h-4 w-4" /></Button></section>;
}

function Empty({ icon, title, body, action, onboarding = false }: { icon: ReactNode; title: string; body: string; action: ReactNode; onboarding?: boolean }) {
  return <div className={`flex min-h-72 flex-col items-center justify-center rounded-md border p-8 text-center ${onboarding ? "border-amber-300 bg-amber-50 text-amber-950" : "border-dashed bg-muted/20"}`}><div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-md ${onboarding ? "bg-amber-100 text-amber-900" : "bg-primary/10 text-primary"}`}>{icon}</div><h2 className="text-lg font-semibold">{title}</h2><p className={`mt-2 max-w-lg text-sm ${onboarding ? "text-amber-900" : "text-muted-foreground"}`}>{body}</p><div className="mt-5">{action}</div></div>;
}
